import React from 'react';
import {
  Button, Container, Stack, Box, Typography, Theme, Skeleton, Alert, AlertTitle,
} from '@mui/material';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';

export type FieldList = [[string, React.ReactElement], ...[string, React.ReactElement][]];

const TripReviewFormPage = ({
  subtitle = undefined,
  title = undefined,
  description = undefined,
  onBack = undefined,
  steps,
  activeStep = 1,
  fields = undefined,
  error = null,
  nextButton,
}: {
  subtitle?: React.ReactNode,
  title?: React.ReactElement,
  description?: React.ReactElement,
  onBack?: () => any,
  steps: number,
  activeStep: number,
  fields?: FieldList,
  error?: ApiError | null,
  nextButton: React.ReactElement,
}) => (
  <Container maxWidth="md" sx={{ my: 8.25 }}>
    <Stack
      spacing={3}
      sx={
        (theme: Theme) => {
          const mobileToolbarHeight = theme.mixins.toolbarLight.minHeight;
          const desktopToolbarHeight = (theme.mixins.toolbarLight as any)['@media (min-width:1100px)'].minHeight;
          const mobileStepperHeight = 84; // hardcoded from inspector
          return {
            py: 2.75,
            minHeight: `calc(100vh - ${mobileToolbarHeight}px - ${mobileStepperHeight}px)`,
            [theme.breakpoints.up('desktop')]: {
              minHeight: `calc(100vh - ${desktopToolbarHeight}px - ${mobileStepperHeight}px)`,
            },
          };
        }
      }
    >
      <Stack spacing={4}>

        <Box>
          {subtitle && (
            <Typography variant="body">
              {subtitle}
            </Typography>
          )}

          <Typography
            variant="h2"
          >
            {title || 'Trip Review'}
          </Typography>
        </Box>

        {description}
      </Stack>

      {fields && (
        <Box>
          <Typography variant="caption">
            STEP
            {' '}
            {activeStep}
          </Typography>
          <Typography
            variant="bodyBold"
            sx={{
              mb: 1,
            }}
          >
            {fields[0][0]}
          </Typography>
          {fields[0][1]}
        </Box>
      )}

      {fields && fields.map((field, index) => {
        if (index === 0) return null;
        return (
          <Box key={field[0]}>
            <Typography variant="bodyBold">
              {field[0]}
            </Typography>
            {field[1]}
          </Box>
        );
      })}

      {error && (
        <Alert severity="error">
          <AlertTitle>Review Error</AlertTitle>
          {error.message}
          <br />
          Code:
          {' '}
          {error.code}
        </Alert>
      )}

      <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" spacing="20px">
          <Button
            variant="outlined"
            onClick={onBack}
            disabled={!onBack}
          >
            Back
          </Button>
          {nextButton}
        </Stack>

        <Typography variant="caption">
          {activeStep}
          {' '}
          /
          {' '}
          {steps}
        </Typography>
      </Box>

    </Stack>
  </Container>
);

TripReviewFormPage.Skeleton = () => (
  <TripReviewFormPage
    subtitle={<Skeleton width={200} />}
    title={<Skeleton width={150} />}
    description={(
      <>
        <Skeleton sx={{
          maxWidth: '75%',
        }}
        />

        <Typography>
          <Skeleton />
          <Skeleton width="90%" />
          <Skeleton width="70%" />
        </Typography>

        <Typography>
          <Skeleton width={50} />
          <Skeleton width="70%" />
          <Skeleton width={150} />
        </Typography>

      </>
    )}
    activeStep={1}
    steps={4}
    nextButton={<Button variant="outlined" disabled>Next</Button>}
  />
);

export default TripReviewFormPage;
